import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiEdit2,
    FiEye,
    FiLoader,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiTrash2,
    FiUsers,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    deleteSupplier,
    fetchSuppliers,
    supplierErrorSelector,
    supplierListSelector,
    supplierLoadingSelector,
    supplierPaginationSelector,
    supplierSummarySelector,
    toggleSupplierStatus,
} from "../../../../../../store/billing/suppliers/supplierSlice";

import "./Supplier.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const PAGE_SIZE_OPTIONS = [
    10,
    20,
    50,
    100,
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};

const getSupplierName = (
    supplier
) =>
    supplier?.supplierName ||
    supplier?.companyName ||
    "-";

const getSupplierMobile = (
    supplier
) =>
    supplier?.mobile ||
    "-";

const getSupplierEmail = (
    supplier
) =>
    supplier?.email ||
    "-";

const getSupplierGSTIN = (
    supplier
) =>
    supplier?.gstin ||
    "-";

const getSupplierAddress = (
    supplier
) => {
    const address =
        supplier?.billingAddress;

    if (!address) {
        return "-";
    }

    if (
        typeof address ===
        "string"
    ) {
        return address;
    }

    const parts = [
        address?.addressLine1,
        address?.addressLine2,
        address?.city,
        address?.state,
        address?.pincode,
    ].filter(Boolean);

    return parts.length
        ? parts.join(", ")
        : "-";
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SupplierListPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const suppliers =
        useSelector(
            supplierListSelector
        ) || [];

    const pagination =
        useSelector(
            supplierPaginationSelector
        ) || {};

    const summary =
        useSelector(
            supplierSummarySelector
        ) || {};

    const loading =
        useSelector(
            supplierLoadingSelector
        ) || {};

    const errors =
        useSelector(
            supplierErrorSelector
        ) || {};

    /*
    |--------------------------------------------------------------------------
    | FILTER STATE
    |--------------------------------------------------------------------------
    */

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        status,
        setStatus,
    ] = useState("");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);

    const [
        actionId,
        setActionId,
    ] = useState("");

    const [
        localError,
        setLocalError,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadSuppliers =
        useCallback(
            async () => {
                try {
                    setLocalError("");

                    await dispatch(
                        fetchSuppliers({
                            page,
                            limit,
                            search:
                                search.trim(),
                            status,
                        })
                    ).unwrap();
                } catch (
                error
                ) {
                    setLocalError(
                        typeof error ===
                            "string"
                            ? error
                            : error?.message ||
                            "Unable to load suppliers."
                    );
                }
            },
            [
                dispatch,
                page,
                limit,
                search,
                status,
            ]
        );

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        loadSuppliers();
                    },
                    350
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            loadSuppliers,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const normalizedSummary =
        useMemo(
            () => ({
                totalSuppliers:
                    toNumber(
                        summary
                            ?.totalSuppliers
                    ),

                activeSuppliers:
                    toNumber(
                        summary
                            ?.activeSuppliers
                    ),

                inactiveSuppliers:
                    toNumber(
                        summary
                            ?.inactiveSuppliers
                    ),

                totalOpeningBalance:
                    roundMoney(
                        summary
                            ?.totalOpeningBalance
                    ),
            }),
            [
                summary,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | NAVIGATION
    |--------------------------------------------------------------------------
    */

    const handleCreate =
        () => {
            history.push(
                "/billing/suppliers/create"
            );
        };

    const handleView = (
        supplier
    ) => {
        if (
            !supplier?._id
        ) {
            return;
        }

        history.push(
            `/billing/suppliers/${supplier._id}`
        );
    };

    const handleEdit = (
        supplier
    ) => {
        if (
            !supplier?._id
        ) {
            return;
        }

        history.push(
            `/billing/suppliers/${supplier._id}/edit`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | TOGGLE STATUS
    |--------------------------------------------------------------------------
    */

    const handleToggleStatus =
        async (
            supplier
        ) => {
            if (
                !supplier?._id
            ) {
                return;
            }

            const currentStatus =
                String(
                    supplier?.status ||
                    ""
                ).toUpperCase();

            const nextStatus =
                currentStatus ===
                    "ACTIVE"
                    ? "INACTIVE"
                    : "ACTIVE";

            const approved =
                window.confirm(
                    `Change supplier status to ${nextStatus}?`
                );

            if (!approved) {
                return;
            }

            try {
                setActionId(
                    supplier._id
                );

                setLocalError("");

                await dispatch(
                    toggleSupplierStatus(
                        supplier._id
                    )
                ).unwrap();

                await loadSuppliers();
            } catch (
            error
            ) {
                setLocalError(
                    typeof error ===
                        "string"
                        ? error
                        : error?.message ||
                        "Unable to change supplier status."
                );
            } finally {
                setActionId("");
            }
        };

    /*
    |--------------------------------------------------------------------------
    | DELETE
    |--------------------------------------------------------------------------
    */

    const handleDelete =
        async (
            supplier
        ) => {
            if (
                !supplier?._id
            ) {
                return;
            }

            const approved =
                window.confirm(
                    `Delete supplier "${getSupplierName(
                        supplier
                    )}"?\n\nSuppliers with purchase bills or ledger transactions cannot be deleted.`
                );

            if (!approved) {
                return;
            }

            try {
                setActionId(
                    supplier._id
                );

                setLocalError("");

                await dispatch(
                    deleteSupplier(
                        supplier._id
                    )
                ).unwrap();

                await loadSuppliers();
            } catch (
            error
            ) {
                setLocalError(
                    typeof error ===
                        "string"
                        ? error
                        : error?.message ||
                        "Unable to delete supplier."
                );
            } finally {
                setActionId("");
            }
        };

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const totalRecords =
        toNumber(
            pagination?.total
        );

    const totalPages =
        Math.max(
            toNumber(
                pagination?.totalPages,
                1
            ),
            1
        );

    const currentPage =
        Math.max(
            toNumber(
                pagination?.page,
                page
            ),
            1
        );

    const startRecord =
        totalRecords >
            0
            ? (
                currentPage -
                1
            ) *
            limit +
            1
            : 0;

    const endRecord =
        Math.min(
            currentPage *
            limit,
            totalRecords
        );

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="supplier-page">
            {/* HEADER */}

            <header className="supplier-header">
                <div className="supplier-heading">
                    <span className="supplier-heading-icon">
                        <FiUsers />
                    </span>

                    <div>
                        <h1>
                            Suppliers
                        </h1>

                        <p>
                            Manage supplier master data, tax details, payment terms and opening balances.
                        </p>
                    </div>
                </div>

                <div className="supplier-header-actions">
                    <button
                        type="button"
                        className="supplier-button"
                        disabled={
                            loading.list
                        }
                        onClick={
                            loadSuppliers
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.list
                                    ? "supplier-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                    <button
                        type="button"
                        className="supplier-button supplier-button-primary"
                        onClick={
                            handleCreate
                        }
                    >
                        <FiPlus />

                        Add Supplier
                    </button>
                </div>
            </header>

            {/* SUMMARY */}

            <section className="supplier-summary-grid">
                <div className="supplier-summary-card">
                    <span>
                        Total Suppliers
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .totalSuppliers
                        }
                    </strong>

                    <small>
                        Supplier master records
                    </small>
                </div>

                <div className="supplier-summary-card supplier-summary-active">
                    <span>
                        Active
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .activeSuppliers
                        }
                    </strong>

                    <small>
                        Available for purchases
                    </small>
                </div>

                <div className="supplier-summary-card supplier-summary-inactive">
                    <span>
                        Inactive
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .inactiveSuppliers
                        }
                    </strong>

                    <small>
                        Disabled suppliers
                    </small>
                </div>

                <div className="supplier-summary-card supplier-summary-opening">
                    <span>
                        Opening Balance
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .totalOpeningBalance
                        )}
                    </strong>

                    <small>
                        Initial supplier payable
                    </small>
                </div>
            </section>

            {/* FILTER */}

            <section className="supplier-toolbar">
                <div className="supplier-search">
                    <FiSearch />

                    <input
                        type="text"
                        value={
                            search
                        }
                        placeholder="Search supplier, mobile, company, GSTIN or code..."
                        onChange={(
                            event
                        ) => {
                            setPage(1);

                            setSearch(
                                event
                                    .target
                                    .value
                            );
                        }}
                    />
                </div>

                <select
                    value={
                        status
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setStatus(
                            event
                                .target
                                .value
                        );
                    }}
                >
                    <option value="">
                        All Status
                    </option>

                    <option value="ACTIVE">
                        Active
                    </option>

                    <option value="INACTIVE">
                        Inactive
                    </option>
                </select>

                <button
                    type="button"
                    className="supplier-button"
                    onClick={() => {
                        setSearch("");
                        setStatus("");
                        setPage(1);
                    }}
                >
                    Clear
                </button>
            </section>

            {/* ERROR */}

            {(localError ||
                errors.list ||
                errors.toggleStatus ||
                errors.delete) && (
                    <div className="supplier-alert">
                        {localError ||
                            errors.list ||
                            errors.toggleStatus ||
                            errors.delete}
                    </div>
                )}

            {/* LIST */}

            <section className="supplier-card">
                {loading.list &&
                    suppliers.length ===
                    0 ? (
                    <div className="supplier-empty">
                        <FiLoader className="supplier-spinner" />

                        <h3>
                            Loading suppliers...
                        </h3>
                    </div>
                ) : suppliers.length ===
                    0 ? (
                    <div className="supplier-empty">
                        <FiUsers />

                        <h3>
                            No suppliers found
                        </h3>

                        <p>
                            Add your first supplier before creating purchase bills.
                        </p>

                        <button
                            type="button"
                            className="supplier-button supplier-button-primary"
                            onClick={
                                handleCreate
                            }
                        >
                            <FiPlus />

                            Add Supplier
                        </button>
                    </div>
                ) : (
                    <div className="supplier-table-wrapper">
                        <table className="supplier-table">
                            <thead>
                                <tr>
                                    <th>
                                        Code
                                    </th>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        Contact
                                    </th>

                                    <th>
                                        GSTIN
                                    </th>

                                    <th>
                                        Address
                                    </th>

                                    <th className="text-right">
                                        Credit Limit
                                    </th>

                                    <th className="text-right">
                                        Opening Balance
                                    </th>

                                    <th>
                                        Payment Terms
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Created
                                    </th>

                                    <th className="text-right">
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {suppliers.map(
                                    (
                                        supplier,
                                        index
                                    ) => {
                                        const supplierStatus =
                                            String(
                                                supplier?.status ||
                                                "ACTIVE"
                                            ).toUpperCase();

                                        const processing =
                                            actionId ===
                                            supplier?._id;

                                        return (
                                            <tr
                                                key={
                                                    supplier?._id ||
                                                    index
                                                }
                                            >
                                                {/* CODE */}

                                                <td>
                                                    <button
                                                        type="button"
                                                        className="supplier-link"
                                                        onClick={() =>
                                                            handleView(
                                                                supplier
                                                            )
                                                        }
                                                    >
                                                        {supplier
                                                            ?.supplierCode ||
                                                            "-"}
                                                    </button>
                                                </td>

                                                {/* SUPPLIER */}

                                                <td>
                                                    <div className="supplier-name-cell">
                                                        <strong>
                                                            {getSupplierName(
                                                                supplier
                                                            )}
                                                        </strong>

                                                        {supplier
                                                            ?.companyName &&
                                                            supplier
                                                                ?.companyName !==
                                                            supplier
                                                                ?.supplierName && (
                                                                <span>
                                                                    {
                                                                        supplier.companyName
                                                                    }
                                                                </span>
                                                            )}
                                                    </div>
                                                </td>

                                                {/* CONTACT */}

                                                <td>
                                                    <div className="supplier-contact-cell">
                                                        <strong>
                                                            {getSupplierMobile(
                                                                supplier
                                                            )}
                                                        </strong>

                                                        <span>
                                                            {getSupplierEmail(
                                                                supplier
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* GST */}

                                                <td>
                                                    {getSupplierGSTIN(
                                                        supplier
                                                    )}
                                                </td>

                                                {/* ADDRESS */}

                                                <td>
                                                    <div className="supplier-address-cell">
                                                        {getSupplierAddress(
                                                            supplier
                                                        )}
                                                    </div>
                                                </td>

                                                {/* CREDIT LIMIT */}

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        supplier
                                                            ?.creditLimit
                                                    )}
                                                </td>

                                                {/* OPENING */}

                                                <td className="text-right">
                                                    <strong
                                                        className={
                                                            toNumber(
                                                                supplier
                                                                    ?.openingBalance
                                                            ) >
                                                                0
                                                                ? "supplier-opening-balance"
                                                                : ""
                                                        }
                                                    >
                                                        {formatCurrency(
                                                            supplier
                                                                ?.openingBalance
                                                        )}
                                                    </strong>
                                                </td>

                                                {/* TERMS */}

                                                <td>
                                                    {toNumber(
                                                        supplier
                                                            ?.paymentTermDays
                                                    ) >
                                                        0
                                                        ? `${toNumber(
                                                            supplier
                                                                ?.paymentTermDays
                                                        )} days`
                                                        : "Immediate"}
                                                </td>

                                                {/* STATUS */}

                                                <td>
                                                    <button
                                                        type="button"
                                                        disabled={
                                                            processing
                                                        }
                                                        className={`supplier-status supplier-status-${supplierStatus.toLowerCase()}`}
                                                        onClick={() =>
                                                            handleToggleStatus(
                                                                supplier
                                                            )
                                                        }
                                                    >
                                                        {processing &&
                                                            loading
                                                                .toggleStatus ? (
                                                            <FiLoader className="supplier-spinner" />
                                                        ) : null}

                                                        {
                                                            supplierStatus
                                                        }
                                                    </button>
                                                </td>

                                                {/* CREATED */}

                                                <td>
                                                    {formatDate(
                                                        supplier
                                                            ?.createdAt
                                                    )}
                                                </td>

                                                {/* ACTIONS */}

                                                <td className="text-right">
                                                    <div className="supplier-actions">
                                                        <button
                                                            type="button"
                                                            title="View"
                                                            className="supplier-action-button"
                                                            disabled={
                                                                processing
                                                            }
                                                            onClick={() =>
                                                                handleView(
                                                                    supplier
                                                                )
                                                            }
                                                        >
                                                            <FiEye />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            title="Edit"
                                                            className="supplier-action-button supplier-action-edit"
                                                            disabled={
                                                                processing
                                                            }
                                                            onClick={() =>
                                                                handleEdit(
                                                                    supplier
                                                                )
                                                            }
                                                        >
                                                            <FiEdit2 />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            title="Delete"
                                                            className="supplier-action-button supplier-action-delete"
                                                            disabled={
                                                                processing
                                                            }
                                                            onClick={() =>
                                                                handleDelete(
                                                                    supplier
                                                                )
                                                            }
                                                        >
                                                            {processing &&
                                                                loading.delete ? (
                                                                <FiLoader className="supplier-spinner" />
                                                            ) : (
                                                                <FiTrash2 />
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PAGINATION */}

                <footer className="supplier-pagination">
                    <div>
                        <span>
                            Showing{" "}
                            <strong>
                                {
                                    startRecord
                                }
                            </strong>
                            {" - "}
                            <strong>
                                {
                                    endRecord
                                }
                            </strong>
                            {" of "}
                            <strong>
                                {
                                    totalRecords
                                }
                            </strong>
                        </span>

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setPage(1);

                                setLimit(
                                    Number(
                                        event
                                            .target
                                            .value
                                    )
                                );
                            }}
                        >
                            {PAGE_SIZE_OPTIONS.map(
                                (
                                    size
                                ) => (
                                    <option
                                        key={
                                            size
                                        }
                                        value={
                                            size
                                        }
                                    >
                                        {size}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div>
                        <button
                            type="button"
                            disabled={
                                currentPage <=
                                1 ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.max(
                                        currentPage -
                                        1,
                                        1
                                    )
                                )
                            }
                        >
                            Previous
                        </button>

                        <span>
                            Page{" "}
                            <strong>
                                {
                                    currentPage
                                }
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {
                                    totalPages
                                }
                            </strong>
                        </span>

                        <button
                            type="button"
                            disabled={
                                currentPage >=
                                totalPages ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.min(
                                        currentPage +
                                        1,
                                        totalPages
                                    )
                                )
                            }
                        >
                            Next
                        </button>
                    </div>
                </footer>
            </section>
        </div>
    );
};

export default SupplierListPage;